import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Building2, Calendar, Euro, ExternalLink, Pencil, Trash2, Clock, User, MessageSquare, TrendingUp, History, Plus, Loader2, ChevronDown, Check, Circle, ArrowRight, Users, FileText, CheckCircle2, AlertCircle, AlertTriangle, Lightbulb, X, Mic, Link } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { toast } from 'sonner';
import { useUser } from '../context/UserContext';
import OpportunityDialog from '../components/OpportunityDialog';
import ActivityDialog from '../components/ActivityDialog';
import DeleteDialog from '../components/DeleteDialog';
import {
  formatEuro as fmtEuro,
  computeDiscountPercent,
  SCOPE_LABEL,
  SCOPE_BADGE_CLS,
} from '../utils/money';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Pipeline stages in order - Phase 4 is "Result" (Ganada or Perdida)
const PIPELINE_STAGES = ["Detectada", "Propuesta", "Presentada", "Resultado"];
const STAGES = ["Detectada", "Propuesta", "Presentada", "Ganada", "Perdida", "Descartada"];

// Stage colors for badges
const stageColors = {
  'Detectada': 'bg-blue-100 text-blue-800',
  'Propuesta': 'bg-yellow-100 text-yellow-800',
  'Presentada': 'bg-purple-100 text-purple-800',
  'Ganada': 'bg-green-100 text-green-800',
  'Perdida': 'bg-red-100 text-red-800',
  'Descartada': 'bg-gray-100 text-gray-800',
  // Legacy stages mapping
  'Lead': 'bg-blue-100 text-blue-800',
  'Qualified': 'bg-yellow-100 text-yellow-800',
  'Proposal': 'bg-purple-100 text-purple-800',
  'Negotiation': 'bg-orange-100 text-orange-800',
  'Won': 'bg-green-100 text-green-800',
  'Lost': 'bg-red-100 text-red-800',
};

// Stepper stage colors (solid colors for the circles)
const stepperColors = {
  'Detectada': { active: 'bg-blue-600', completed: 'bg-blue-600', text: 'text-blue-600' },
  'Propuesta': { active: 'bg-amber-500', completed: 'bg-amber-500', text: 'text-amber-500' },
  'Presentada': { active: 'bg-purple-600', completed: 'bg-purple-600', text: 'text-purple-600' },
  'Resultado': { active: 'bg-slate-600', completed: 'bg-slate-600', text: 'text-slate-600' },
  'Ganada': { active: 'bg-emerald-600', completed: 'bg-emerald-600', text: 'text-emerald-600' },
  'Perdida': { active: 'bg-red-600', completed: 'bg-red-600', text: 'text-red-600' },
  'Descartada': { active: 'bg-gray-500', completed: 'bg-gray-500', text: 'text-gray-500' },
};

const relevanceColors = {
  'Alta': 'bg-red-100 text-red-800',
  'Media': 'bg-yellow-100 text-yellow-800',
  'Baja': 'bg-green-100 text-green-800',
};

const OpportunityDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isEditor, currentUser } = useUser();
  
  const [opportunity, setOpportunity] = useState(null);
  const [company, setCompany] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  
  // State change dialog
  const [stageChangeDialogOpen, setStageChangeDialogOpen] = useState(false);
  const [targetStage, setTargetStage] = useState(null);
  const [stageChangeData, setStageChangeData] = useState({});
  const [stageChangeLoading, setStageChangeLoading] = useState(false);
  const [stageChangeComment, setStageChangeComment] = useState('');
  
  // Activity detail sheet
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [activityDetailOpen, setActivityDetailOpen] = useState(false);
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [oppRes, activitiesRes, contactsRes] = await Promise.all([
        axios.get(`${API}/opportunities/${id}`),
        axios.get(`${API}/activities`, { params: { opportunity_id: id } }),
        axios.get(`${API}/contacts`)
      ]);
      
      setOpportunity(oppRes.data);
      setActivities(activitiesRes.data.sort((a, b) => new Date(b.date) - new Date(a.date)));
      setContacts(contactsRes.data);
      
      // Fetch company if exists
      if (oppRes.data.company_id) {
        try {
          const companyRes = await axios.get(`${API}/companies/${oppRes.data.company_id}`);
          setCompany(companyRes.data);
        } catch (err) {
          console.log('Company not found');
        }
      }
    } catch (error) {
      console.error('Error fetching opportunity:', error);
      toast.error('Error al cargar la oportunidad');
      navigate('/pipeline');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/opportunities/${id}`, {
        headers: { 'X-User-Email': currentUser?.email }
      });
      toast.success('Oportunidad eliminada');
      navigate('/pipeline');
    } catch (error) {
      console.error('Error deleting opportunity:', error);
      toast.error('Error al eliminar la oportunidad');
    }
  };

  const formatCurrency = (value) => {
    // Unified formatter across the app: "123.431 €" (see /utils/money.js).
    return fmtEuro(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-ES');
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('es-ES');
  };

  // Get contact name by ID
  const getContactName = (contactId) => {
    if (!contactId) return null;
    const contact = contacts.find(c => c.id === contactId);
    if (!contact) return null;
    return `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || contact.email;
  };

  // Get contact names from array of IDs
  const getContactNames = (contactIds) => {
    if (!contactIds || !Array.isArray(contactIds) || contactIds.length === 0) return [];
    return contactIds.map(id => getContactName(id)).filter(Boolean);
  };

  // Check if activity is documented
  // An activity is documented if it has ANY of: summary, notes, key_points, decisions, next_steps
  const isActivityDocumented = (activity) => {
    const hasSummary = activity.summary?.trim();
    const hasNotes = activity.notes?.trim();
    const hasKeyPoints = activity.key_points?.length > 0;
    const hasDecisions = activity.decisions?.length > 0;
    const hasNextSteps = activity.next_steps?.length > 0;
    
    return !!(hasSummary || hasNotes || hasKeyPoints || hasDecisions || hasNextSteps);
  };

  // Get activity type label
  const getActivityTypeLabel = (type) => {
    const labels = {
      'meeting': 'Reunión',
      'call': 'Llamada',
      'email': 'Email',
      'visit': 'Visita',
      'other': 'Otro'
    };
    return labels[type] || type;
  };

  // Open activity detail
  const handleActivityClick = (activity) => {
    setSelectedActivity(activity);
    setActivityDetailOpen(true);
  };

  // Stage change required fields configuration
  // Matriz FTF Feb 2026: los campos "auto" (final_awardee, awardee_discount,
  // global_position para Pública Ganada; award_amount para TYPSA Ganada) NO se
  // piden aquí porque se rellenan solos al guardar en OpportunityDialog.
  // scope: 'publica' | 'privada' | 'typsa'
  const getStageRequiredFields = (stage, scope) => {
    const isTypsaOpp = scope === 'typsa';
    const isPublica = scope === 'publica';
    const baseFields = {
      Propuesta: [
        { key: 'service_types', label: 'Tipos de servicios', type: 'checkbox-list', options: ['BIM','GIS','Gobernanza del Dato','Inteligencia Artificial','Software a Medida','Gemelos Digitales','Visualización Avanzada','Otros'], showIfMissing: true },
        { key: 'other_service_type', label: 'Descripción "Otros"', type: 'text', showIfMissing: true, requiresOther: true },
        { key: 'relevance', label: 'Relevancia', type: 'select', options: ['Alta', 'Media', 'Baja'], showIfMissing: true },
        { key: 'relevance_comment', label: 'Comentario de Relevancia', type: 'textarea', showIfMissing: true },
        { key: 'link_carpeta_trabajo', label: 'Link Carpeta de trabajo inicial', type: 'text', showIfMissing: true },
      ],
      Presentada: [
        { key: 'service_types', label: 'Tipos de servicios', type: 'checkbox-list', options: ['BIM','GIS','Gobernanza del Dato','Inteligencia Artificial','Software a Medida','Gemelos Digitales','Visualización Avanzada','Otros'], showIfMissing: true },
        { key: 'other_service_type', label: 'Descripción "Otros"', type: 'text', showIfMissing: true, requiresOther: true },
        { key: 'relevance', label: 'Relevancia', type: 'select', options: ['Alta', 'Media', 'Baja'], showIfMissing: true },
        { key: 'relevance_comment', label: 'Comentario de Relevancia', type: 'textarea', showIfMissing: true },
        { key: 'bid_modality', label: 'Modalidad de participación', type: 'select', options: ['solo', 'ute', 'subcontratista'], showIfMissing: true, hideIfTypsa: true },
        { key: 'offered_amount', label: 'Importe Ofertado (€)', type: 'number', showIfMissing: true },
        { key: 'delivery_date', label: 'Fecha Real de Entrega', type: 'date', showIfMissing: true },
        { key: 'bidder', label: 'Entidad Licitadora', type: 'text', showIfMissing: true, hideIfTypsa: true, hideIfSolo: true },
        { key: 'probability', label: 'Probabilidad', type: 'select', options: ['Alta', 'Media', 'Baja'], showIfMissing: true },
        { key: 'link_sharepoint', label: 'Link SharePoint de la oferta', type: 'text', showIfMissing: true, hideIfTypsa: true },
      ],
      Ganada: isTypsaOpp ? [
        // TYPSA: award_amount es auto (= offered_amount); solo pedir relevance si falta.
        { key: 'relevance', label: 'Relevancia', type: 'select', options: ['Alta', 'Media', 'Baja'], showIfMissing: true },
      ] : isPublica ? [
        // Pública Ganada: adjudicatario/baja/global_position son auto en el form.
        // award_amount TAMBIÉN es auto (= offered_amount).
        { key: 'relevance', label: 'Relevancia', type: 'select', options: ['Alta', 'Media', 'Baja'], showIfMissing: true },
        { key: 'probability', label: 'Probabilidad', type: 'select', options: ['Alta', 'Media', 'Baja'], showIfMissing: true },
        { key: 'number_of_bidders', label: 'Nº de Licitadores', type: 'number', showIfMissing: true },
        { key: 'technical_position', label: 'Posición Técnica', type: 'text', showIfMissing: true },
        { key: 'economic_position', label: 'Posición Económica', type: 'text', showIfMissing: true },
      ] : [
        // Privada Ganada: nada extra — award_amount es auto (= ofertado).
        { key: 'relevance', label: 'Relevancia', type: 'select', options: ['Alta', 'Media', 'Baja'], showIfMissing: true },
        { key: 'probability', label: 'Probabilidad', type: 'select', options: ['Alta', 'Media', 'Baja'], showIfMissing: true },
      ],
      Perdida: isTypsaOpp ? [
        { key: 'relevance', label: 'Relevancia', type: 'select', options: ['Alta', 'Media', 'Baja'], showIfMissing: true },
      ] : isPublica ? [
        // Pública Perdida: los 7 campos, solo si faltan.
        { key: 'relevance', label: 'Relevancia', type: 'select', options: ['Alta', 'Media', 'Baja'], showIfMissing: true },
        { key: 'probability', label: 'Probabilidad', type: 'select', options: ['Alta', 'Media', 'Baja'], showIfMissing: true },
        { key: 'final_awardee', label: 'Adjudicatario Final', type: 'text', showIfMissing: true },
        { key: 'award_amount', label: 'Importe Adjudicación (€)', type: 'number', showIfMissing: true },
        { key: 'awardee_discount', label: 'Baja Adjudicatario (%)', type: 'number', showIfMissing: true },
        { key: 'number_of_bidders', label: 'Nº de Licitadores', type: 'number', showIfMissing: true },
        { key: 'global_position', label: 'Posición Global', type: 'text', showIfMissing: true },
        { key: 'technical_position', label: 'Posición Técnica', type: 'text', showIfMissing: true },
        { key: 'economic_position', label: 'Posición Económica', type: 'text', showIfMissing: true },
      ] : [
        // Privada Perdida: todos opcionales, solo si faltan.
        { key: 'relevance', label: 'Relevancia', type: 'select', options: ['Alta', 'Media', 'Baja'], showIfMissing: true },
        { key: 'probability', label: 'Probabilidad', type: 'select', options: ['Alta', 'Media', 'Baja'], showIfMissing: true },
        { key: 'final_awardee', label: 'Adjudicatario Final', type: 'text', showIfMissing: true },
        { key: 'award_amount', label: 'Importe Adjudicación (€)', type: 'number', showIfMissing: true },
        { key: 'awardee_discount', label: 'Baja Adjudicatario (%)', type: 'number', showIfMissing: true },
        { key: 'number_of_bidders', label: 'Nº de Licitadores', type: 'number', showIfMissing: true },
      ],
      Descartada: [
        { key: 'discard_reason', label: 'Motivo de Descarte', type: 'textarea', showIfMissing: true },
      ],
    };

    return baseFields[stage] || [];
  };

  // Check if opportunity is TYPSA (internal)
  const isTypsaOpportunity = opportunity?.typsa_org_unit_id ? true : false;
  // Scope inferred from company_type or typsa_org_unit_id (Matriz FTF).
  const opportunityScope = isTypsaOpportunity
    ? 'typsa'
    : (company?.company_type === 'publica' ? 'publica' : 'privada');

  const handleStageClick = (newStage) => {
    if (newStage === opportunity.stage) return;
    
    const allFields = getStageRequiredFields(newStage, opportunityScope);
    if (allFields && allFields.length > 0) {
      // Filter fields: show only those that need to be filled based on conditions
      const requiredFields = allFields.filter(field => {
        // Check showIfMissing: only show if the value is currently missing
        if (field.showIfMissing) {
          return !opportunity[field.key];
        }
        // Check showIfNoOfferedAmount: only show if offered_amount is not set
        if (field.showIfNoOfferedAmount) {
          return !opportunity.offered_amount;
        }
        // Always show required fields without conditions
        return true;
      });
      
      if (requiredFields.length > 0) {
        // Initialize with existing data if available
        const initialData = {};
        requiredFields.forEach(field => {
          initialData[field.key] = opportunity[field.key] || '';
        });
        setStageChangeData(initialData);
        setTargetStage(newStage);
        setStageChangeComment('');
        setStageChangeDialogOpen(true);
      } else {
        // All required fields already have values, but still show dialog for comment
        setTargetStage(newStage);
        setStageChangeComment('');
        setStageChangeDialogOpen(true);
      }
    } else {
      // No required fields, but still show dialog for comment
      setTargetStage(newStage);
      setStageChangeComment('');
      setStageChangeDialogOpen(true);
    }
  };

  const confirmStageChange = async (newStage, additionalData, comment = '') => {
    setStageChangeLoading(true);
    try {
      // Build the reason/comment for history
      const reason = comment 
        ? `${comment}` 
        : `Cambio de estado de ${opportunity.stage} a ${newStage}`;
      
      const payload = {
        ...opportunity,
        stage: newStage,
        ...additionalData,
        state_change_reason: reason,
      };
      
      // Clean up fields
      delete payload.id;
      delete payload.created_at;
      delete payload.state_history;
      delete payload.created_by_name;
      delete payload.created_by_email;
      
      await axios.put(`${API}/opportunities/${id}`, payload, {
        headers: { 
          'X-User-Email': currentUser?.email,
          'X-User-Name': currentUser?.name 
        }
      });
      
      toast.success(`Estado cambiado a ${newStage}`);
      setStageChangeDialogOpen(false);
      setTargetStage(null);
      setStageChangeData({});
      setStageChangeComment('');
      fetchData();
    } catch (error) {
      console.error('Error changing stage:', error);
      toast.error(error.response?.data?.detail || 'Error al cambiar el estado');
    } finally {
      setStageChangeLoading(false);
    }
  };

  const handleStageChangeSubmit = () => {
    const allFields = getStageRequiredFields(targetStage, opportunityScope);

    // Filtro respetando visibilidad (hideIfTypsa, hideIfSolo, requiresOther, showIfMissing).
    const visibleFields = allFields.filter(field => {
      if (field.hideIfTypsa && isTypsaOpportunity) return false;
      if (field.hideIfSolo && (opportunity.bid_modality === 'solo' || stageChangeData.bid_modality === 'solo')) return false;
      if (field.requiresOther && !(stageChangeData.service_types || []).includes('Otros')) return false;
      if (field.showIfMissing) {
        const v = opportunity[field.key];
        if (Array.isArray(v)) return v.length === 0;
        return v === null || v === undefined || v === '';
      }
      if (field.showIfNoOfferedAmount) return !opportunity.offered_amount;
      return true;
    });

    // Validate visible required fields (arrays cuentan como vacío si length===0).
    for (const field of visibleFields) {
      const v = stageChangeData[field.key];
      const isEmpty = Array.isArray(v) ? v.length === 0 : (!v && v !== 0);
      if (isEmpty) {
        toast.error(`El campo "${field.label}" es obligatorio`);
        return;
      }
    }

    // Convert numeric fields
    const processedData = { ...stageChangeData };
    visibleFields.forEach(field => {
      if (field.type === 'number' && processedData[field.key]) {
        processedData[field.key] = parseFloat(processedData[field.key]);
      }
    });

    confirmStageChange(targetStage, processedData, stageChangeComment);
  };

  const renderLink = (url, label) => {
    if (!url) return null;
    return (
      <a 
        href={url} 
        target="_blank" 
        rel="noopener noreferrer"
        className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
      >
        <ExternalLink className="w-4 h-4" />
        {label}
      </a>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!opportunity) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Oportunidad no encontrada</p>
        <Button onClick={() => navigate('/pipeline')} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver al Pipeline
        </Button>
      </div>
    );
  }

  const title = opportunity.title || opportunity.name || 'Sin título';
  const stage = opportunity.stage || 'Detectada';
  const isResultStage = stage === 'Ganada' || stage === 'Perdida';
  const isDiscarded = stage === 'Descartada';

  // Map actual stage to stepper position
  const getStepperIndex = (actualStage) => {
    if (actualStage === 'Ganada' || actualStage === 'Perdida') return 3; // "Resultado" position
    return PIPELINE_STAGES.indexOf(actualStage);
  };
  
  const currentStageIndex = getStepperIndex(stage);
  const isInPipeline = currentStageIndex !== -1;

  // Render the Pipeline Stepper
  const renderPipelineStepper = () => {
    const stages = [...PIPELINE_STAGES];
    
    return (
      <Card className="border-0 shadow-sm bg-gradient-to-r from-slate-50 to-white" data-testid="pipeline-stepper">
        <CardContent className="py-6">
          {/* Stepper */}
          <div className="relative">
            {/* Progress line background */}
            <div className="absolute top-5 left-0 right-0 h-0.5 bg-slate-200" />
            
            {/* Progress line filled */}
            {isInPipeline && !isDiscarded && (
              <div 
                className="absolute top-5 left-0 h-0.5 bg-slate-400 transition-all duration-500"
                style={{ width: `${(currentStageIndex / (stages.length - 1)) * 100}%` }}
              />
            )}
            
            {/* Stage nodes */}
            <div className="relative flex justify-between">
              {stages.map((s, index) => {
                const isResultNode = s === 'Resultado';
                const isCompleted = isInPipeline && !isDiscarded && index < currentStageIndex;
                const isCurrent = isResultNode 
                  ? isResultStage 
                  : s === stage;
                const isFuture = isInPipeline && !isDiscarded && index > currentStageIndex;
                
                // For the "Resultado" node, use the actual stage color (Ganada/Perdida)
                let colors;
                let displayLabel = s;
                if (isResultNode) {
                  if (isResultStage) {
                    colors = stepperColors[stage]; // Use Ganada or Perdida colors
                    displayLabel = stage; // Show actual stage name
                  } else {
                    colors = stepperColors['Resultado'];
                    displayLabel = 'Ganada / Perdida';
                  }
                } else {
                  colors = stepperColors[s];
                }
                
                // If stage is Descartada, show all as neutral
                const showAsNeutral = isDiscarded;
                
                return (
                  <div 
                    key={s}
                    className="flex flex-col items-center cursor-pointer group"
                    onClick={() => {
                      if (!isEditor) return;
                      // For "Resultado" node, open dialog to choose between Ganada/Perdida
                      if (isResultNode && !isResultStage) {
                        setTargetStage(null);
                        setStageChangeComment('');
                        setStageChangeDialogOpen(true);
                      } else if (!isResultNode) {
                        handleStageClick(s);
                      }
                    }}
                    data-testid={`stepper-stage-${s.toLowerCase()}`}
                  >
                    {/* Circle */}
                    <div className={`
                      relative flex items-center justify-center rounded-full transition-all duration-300
                      ${isCurrent 
                        ? `w-10 h-10 ${colors.active} text-white shadow-lg ring-4 ring-offset-2 ${
                            stage === 'Ganada' ? 'ring-emerald-200' : 
                            stage === 'Perdida' ? 'ring-red-200' : 'ring-slate-200'
                          }` 
                        : isCompleted && !showAsNeutral
                          ? `w-8 h-8 ${colors.completed} text-white`
                          : 'w-8 h-8 bg-slate-200 text-slate-400'
                      }
                      ${isEditor && !isCurrent ? 'group-hover:scale-110 group-hover:shadow-md' : ''}
                    `}>
                      {isCompleted && !showAsNeutral ? (
                        <Check className="w-4 h-4" />
                      ) : isCurrent ? (
                        stage === 'Ganada' || stage === 'Perdida' ? (
                          stage === 'Ganada' ? <Check className="w-5 h-5" /> : <Circle className="w-4 h-4 fill-current" />
                        ) : (
                          <Circle className="w-4 h-4 fill-current" />
                        )
                      ) : (
                        <span className="text-xs font-semibold">{index + 1}</span>
                      )}
                    </div>
                    
                    {/* Label */}
                    <span className={`
                      mt-2 text-xs font-medium transition-all text-center max-w-[80px]
                      ${isCurrent 
                        ? `${colors.text} font-bold` 
                        : isCompleted && !showAsNeutral
                          ? 'text-slate-600'
                          : 'text-slate-400'
                      }
                      ${isEditor && !isCurrent ? 'group-hover:text-slate-700' : ''}
                    `}>
                      {displayLabel}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Special state: Descartada */}
          {isDiscarded && (
            <div className="mt-6 pt-4 border-t border-dashed border-slate-200 flex items-center justify-center gap-3">
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                <div className="w-3 h-3 rounded-full bg-slate-400" />
                <span className="font-semibold">Descartada</span>
              </div>
            </div>
          )}
          
          {/* Action button for editors */}
          {isEditor && (
            <div className="mt-6 pt-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setTargetStage(null);
                  setStageChangeComment('');
                  setStageChangeDialogOpen(true);
                }}
                className="text-slate-600 hover:text-slate-800 hover:bg-slate-50 border-slate-200"
                data-testid="change-stage-btn"
              >
                <ArrowRight className="w-4 h-4 mr-2" />
                Cambiar estado
              </Button>
              
              {/* Quick action for discard only (not Perdida) */}
              {!isResultStage && !isDiscarded && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleStageClick('Descartada')}
                  className="text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                  data-testid="mark-discarded-btn"
                >
                  Descartar
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6" data-testid="opportunity-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate('/pipeline')}
            data-testid="back-to-pipeline-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{title}</h1>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {(() => {
                // Feb 2026: show scope badge (TYPSA/Privada/Pública) inferred
                // from company_type or typsa_org_unit_id.
                const scope = opportunity.typsa_org_unit_id
                  ? 'typsa'
                  : (company?.company_type === 'publica'
                      ? 'publica'
                      : company ? 'privada' : null);
                if (!scope) return null;
                return (
                  <Badge className={`${SCOPE_BADGE_CLS[scope]} border`} data-testid="opportunity-scope-badge">
                    {SCOPE_LABEL[scope]}
                  </Badge>
                );
              })()}
              {opportunity.relevance && (
                <Badge className={relevanceColors[opportunity.relevance]}>{opportunity.relevance}</Badge>
              )}
              {opportunity.probability && (
                <Badge variant="outline">Prob: {opportunity.probability}</Badge>
              )}
            </div>
            {company && (
              <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1">
                <Building2 className="w-4 h-4" />
                {company.name}
              </p>
            )}
          </div>
        </div>
        
        <div className="flex gap-2">
          {isEditor && (
            <>
              <Button variant="outline" onClick={() => setEditDialogOpen(true)} data-testid="edit-opportunity-btn">
                <Pencil className="w-4 h-4 mr-2" />
                Editar
              </Button>
              <Button variant="destructive" onClick={() => setDeleteDialogOpen(true)} data-testid="delete-opportunity-btn">
                <Trash2 className="w-4 h-4 mr-2" />
                Eliminar
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Pipeline Stepper */}
      {renderPipelineStepper()}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Información General */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Información General</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {opportunity.description && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Descripción</label>
                  <p className="mt-1">{opportunity.description}</p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  {(() => {
                    // Feb 2026 (A4): label depends on scope.
                    const scope = opportunity.typsa_org_unit_id
                      ? 'typsa'
                      : (company?.company_type === 'publica' ? 'publica' : 'privada');
                    if (scope === 'publica') {
                      return (
                        <>
                          <label className="text-sm font-medium text-muted-foreground" title="Importe publicado en el anuncio. No es cartera de TDS.">
                            Presupuesto base de licitación
                          </label>
                          <p className="mt-1 text-lg font-semibold text-slate-700" data-testid="valor-estimado-value">
                            {formatCurrency(opportunity.estimated_value)}
                          </p>
                        </>
                      );
                    }
                    return (
                      <>
                        <label className="text-xs font-medium text-slate-500 flex items-center gap-1" title="Cifra orientativa introducida por el equipo. No se incluye en ningún total de cartera.">
                          <AlertCircle className="w-3 h-3 text-slate-400" />
                          Valor de referencia (interno, no contractual)
                        </label>
                        <p className="mt-1 text-slate-500 italic" data-testid="valor-estimado-value">
                          {opportunity.estimated_value != null ? formatCurrency(opportunity.estimated_value) : 'Sin dato'}
                        </p>
                      </>
                    );
                  })()}
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Fecha Límite de Entrega</label>
                  <p className="mt-1">
                    {formatDate(opportunity.expected_delivery_date || opportunity.expected_close_date)}
                    {opportunity.deadline_time && <span className="ml-2 text-muted-foreground">a las {opportunity.deadline_time}</span>}
                  </p>
                </div>
              </div>

              {opportunity.owner && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Responsable</label>
                  <p className="mt-1 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    {opportunity.owner}
                  </p>
                </div>
              )}

              {/* Tipos de Servicios Involucrados */}
              {opportunity.service_types && opportunity.service_types.length > 0 && (
                <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                  <label className="text-sm font-semibold text-blue-800 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Tipos de Servicios Involucrados
                  </label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {opportunity.service_types.map((service) => (
                      <Badge 
                        key={service} 
                        className="bg-blue-600 text-white hover:bg-blue-700"
                        data-testid={`service-badge-${service.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {service}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Links - Phase Detectada */}
              <div className="space-y-2">
                {renderLink(opportunity.link_carpeta_trabajo, 'Carpeta de Trabajo Inicial')}
                {renderLink(opportunity.link_pliego, 'Ver Pliego')}
                {renderLink(opportunity.link_infografia, 'Ver Infografía')}
                {renderLink(opportunity.link_pdf_resumen, 'Ver PDF Resumen')}
                {renderLink(opportunity.link_notebooklm, 'Ver NotebookLM')}
              </div>
            </CardContent>
          </Card>

          {/* Salto a Producción - Solo para Ganada */}
          {stage === 'Ganada' && (
            <Card className="border-green-200 bg-green-50/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-green-700">
                  <CheckCircle2 className="w-5 h-5" />
                  Salto a Producción
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Proyecto de referencia en TDS</label>
                    <p className="mt-1 font-medium">{opportunity.tds_project_reference || <span className="text-muted-foreground italic">Sin especificar</span>}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Tarea dentro del proyecto</label>
                    <p className="mt-1 font-medium">{opportunity.tds_project_task || <span className="text-muted-foreground italic">Sin especificar</span>}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Información de Propuesta - Visible from Propuesta onwards */}
          {['Propuesta', 'Presentada', 'Ganada', 'Perdida', 'Descartada'].includes(stage) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Preparación de Propuesta</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Relevancia</label>
                    <p className="mt-1 font-semibold">{opportunity.relevance || '-'}</p>
                  </div>
                  {opportunity.relevance_comment && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Comentario de Relevancia</label>
                      <p className="mt-1">{opportunity.relevance_comment}</p>
                    </div>
                  )}
                </div>
                
                {renderLink(opportunity.link_sharepoint, 'Ver SharePoint de la Oferta')}
                
                {opportunity.comments && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Comentarios</label>
                    <p className="mt-1 whitespace-pre-wrap">{opportunity.comments || opportunity.notes}</p>
                  </div>
                )}
                
                {!opportunity.link_sharepoint && !opportunity.comments && !opportunity.relevance && (
                  <p className="text-sm text-muted-foreground">Sin información adicional</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Información de Presentada - Visible from Presentada onwards */}
          {['Presentada', 'Ganada', 'Perdida'].includes(stage) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Presentación de Oferta</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Probabilidades</label>
                    <p className="mt-1 font-semibold">{opportunity.probability || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Importe Ofertado</label>
                    <p className="mt-1 font-semibold">{formatCurrency(opportunity.offered_amount)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Importe TDS</label>
                    <p className="mt-1">{formatCurrency(opportunity.tds_amount)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Fecha de Entrega Real</label>
                    <p className="mt-1">{formatDate(opportunity.delivery_date)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Licitador</label>
                    <p className="mt-1">{opportunity.bidder || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Porcentaje de Bajada</label>
                    {(() => {
                      // Feb 2026 (A5): auto-derived from estimated_value and
                      // offered_amount, ONLY meaningful in Pública.
                      const scope = opportunity.typsa_org_unit_id
                        ? 'typsa'
                        : (company?.company_type === 'publica' ? 'publica' : 'privada');
                      if (scope !== 'publica') {
                        return (
                          <p className="mt-1 text-sm text-muted-foreground italic" data-testid="discount-not-applicable">
                            No aplica: sin presupuesto base publicado
                          </p>
                        );
                      }
                      const computed = computeDiscountPercent(
                        opportunity.estimated_value,
                        opportunity.offered_amount,
                      );
                      if (computed === null) {
                        return <p className="mt-1 text-sm">-</p>;
                      }
                      return (
                        <p
                          className="mt-1 text-lg font-semibold"
                          data-testid="discount-percentage-computed"
                          title="Baja = (presupuesto base − importe ofertado) / presupuesto base × 100"
                        >
                          {`${computed.toString().replace('.', ',')}%`}
                        </p>
                      );
                    })()}
                  </div>
                </div>
                
                {/* Historial de Entregas */}
                {opportunity.delivery_history && opportunity.delivery_history.length > 0 && (
                  <div className="mt-6 pt-4 border-t">
                    <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <History className="w-4 h-4" />
                      Historial de Entregas ({opportunity.delivery_history.length})
                    </label>
                    <div className="mt-2 space-y-2">
                      {opportunity.delivery_history.map((entry, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                          <div className="w-2 h-2 rounded-full bg-blue-500 mt-2 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium text-sm">{formatDate(entry.date)}</span>
                              <span className="text-xs text-muted-foreground">
                                {entry.timestamp ? new Date(entry.timestamp).toLocaleDateString('es-ES') : ''}
                              </span>
                            </div>
                            {entry.note && (
                              <p className="text-sm text-muted-foreground mt-1">{entry.note}</p>
                            )}
                            {entry.created_by_name && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Por {entry.created_by_name}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Resultado Final - Only for Ganada/Perdida */}
          {isResultStage && (
            <Card className={stage === 'Ganada' ? 'border-green-200 bg-green-50/50' : 'border-red-200 bg-red-50/50'}>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Resultado Final
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Adjudicatario Final</label>
                    <p className="mt-1 font-semibold">{opportunity.final_awardee || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Importe Adjudicación</label>
                    <p className="mt-1 font-semibold text-green-600">{formatCurrency(opportunity.award_amount)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Baja Adjudicatario</label>
                    <p className="mt-1">{opportunity.awardee_discount !== null ? `${opportunity.awardee_discount}%` : '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Nº Licitadores</label>
                    <p className="mt-1">{opportunity.number_of_bidders || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Posición Global</label>
                    <p className="mt-1">{opportunity.global_position || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Posición Técnica</label>
                    <p className="mt-1">{opportunity.technical_position || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Posición Económica</label>
                    <p className="mt-1">{opportunity.economic_position || '-'}</p>
                  </div>
                </div>
                
                {opportunity.result_comments && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Comentarios del Resultado</label>
                    <p className="mt-1 whitespace-pre-wrap">{opportunity.result_comments}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Motivo de Descarte - Only for Descartada — Feb 2026 (PR-B B2/B3) */}
          {isDiscarded && (
            <Card className="border-gray-300 bg-gray-50/50" data-testid="discard-info-card">
              <CardHeader>
                <CardTitle className="text-lg">Motivo del Descarte</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Motivo principal</label>
                  <p className="mt-1 font-medium" data-testid="discard-primary-value">
                    {opportunity.discard_reason_primary
                      ? (opportunity.discard_reason_primary === 'sin_clasificar'
                          ? <span className="text-amber-700 italic">Sin clasificar</span>
                          : opportunity.discard_reason_primary)
                      : <span className="text-amber-700 italic">Sin clasificar</span>}
                  </p>
                </div>
                {opportunity.discard_reason_secondary && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Motivo secundario</label>
                    <p className="mt-1">{opportunity.discard_reason_secondary}</p>
                  </div>
                )}
                {opportunity.discard_reason && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Detalle</label>
                    <p className="mt-1 whitespace-pre-wrap">{opportunity.discard_reason}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Resultado del concurso - Only for Descartada (PR-B B3).
              Pública: expanded with all fields.
              Privada/TYPSA: collapsed and reduced to adjudicatario/importe/notas. */}
          {isDiscarded && (() => {
            const scopeR = opportunity.typsa_org_unit_id
              ? 'typsa'
              : (company?.company_type === 'publica' ? 'publica' : 'privada');
            const isPublicR = scopeR === 'publica';
            return (
              <Card className={`${isPublicR ? 'border-green-200 bg-green-50/30' : 'border-slate-200 bg-slate-50/50'}`} data-testid="discarded-result-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      Resultado del concurso
                    </span>
                    {!isPublicR && (
                      <Badge variant="outline" className="text-xs">
                        {opportunity.follow_up_active ? 'Seguimiento activado' : 'Seguimiento desactivado'}
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {!isPublicR && !opportunity.follow_up_active && !opportunity.final_awardee && (
                    <p className="text-xs text-muted-foreground italic">
                      El seguimiento en privadas/TYPSA es opcional. Activa el seguimiento desde el formulario si quieres registrar el resultado.
                    </p>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Adjudicatario final</label>
                      <p className="mt-1">{opportunity.final_awardee || <span className="text-muted-foreground italic">-</span>}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Importe de adjudicación</label>
                      <p className="mt-1">{opportunity.award_amount != null ? formatCurrency(opportunity.award_amount) : <span className="text-muted-foreground italic">-</span>}</p>
                    </div>
                    {isPublicR && (
                      <>
                        <div>
                          <label className="text-sm font-medium text-muted-foreground">Baja del adjudicatario</label>
                          <p className="mt-1">{opportunity.awardee_discount != null ? `${opportunity.awardee_discount}%` : <span className="text-muted-foreground italic">-</span>}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-muted-foreground">Nº de licitadores</label>
                          <p className="mt-1">{opportunity.number_of_bidders || <span className="text-muted-foreground italic">-</span>}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-muted-foreground">Fecha de adjudicación</label>
                          <p className="mt-1">{opportunity.expected_resolution_date || <span className="text-muted-foreground italic">-</span>}</p>
                        </div>
                      </>
                    )}
                  </div>
                  {opportunity.result_comments && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Notas de mercado</label>
                      <p className="mt-1 whitespace-pre-wrap">{opportunity.result_comments}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })()}

          {/* Actividades Relacionadas */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Actividades Relacionadas</CardTitle>
                <CardDescription>{activities.length} actividades</CardDescription>
              </div>
              {isEditor && (
                <Button size="sm" onClick={() => setActivityDialogOpen(true)} data-testid="add-activity-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Añadir Actividad
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {activities.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No hay actividades relacionadas
                </p>
              ) : (
                <div className="space-y-3">
                  {activities.map((activity) => {
                    const isDocumented = isActivityDocumented(activity);
                    const contactNames = getContactNames(activity.contact_ids);
                    const isPast = new Date(activity.date) < new Date();
                    
                    return (
                      <div 
                        key={activity.id}
                        onClick={() => handleActivityClick(activity)}
                        className="p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                        data-testid={`activity-${activity.id}`}
                      >
                        {/* Header row */}
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium">{activity.title || getActivityTypeLabel(activity.type)}</p>
                              <Badge variant="outline" className="text-xs">
                                {getActivityTypeLabel(activity.type)}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              <Calendar className="w-3 h-3 inline mr-1" />
                              {formatDate(activity.date)}
                              {activity.time && ` • ${activity.time}`}
                            </p>
                          </div>
                          
                          {/* Documentation status */}
                          <div className="flex-shrink-0 flex items-center gap-2">
                            {isPast && !isDocumented ? (
                              <>
                                <Badge variant="destructive" className="text-xs">
                                  <AlertCircle className="w-3 h-3 mr-1" />
                                  Sin documentar
                                </Badge>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-6 px-2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/voice-notes/create?activity_id=${activity.id}&company_id=${activity.company_id || ''}`);
                                  }}
                                  title="Documentar con audio"
                                >
                                  <Mic className="w-3 h-3" />
                                </Button>
                              </>
                            ) : isDocumented ? (
                              <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Documentada
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="text-xs">
                                Próxima
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        {/* Details row */}
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          {activity.owner && (
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {activity.owner}
                            </span>
                          )}
                          {contactNames.length > 0 && (
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {contactNames.length === 1 
                                ? contactNames[0] 
                                : `${contactNames[0]} +${contactNames.length - 1}`}
                            </span>
                          )}
                          {activity.location && (
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {activity.location}
                            </span>
                          )}
                          {/* Content Link */}
                          {activity.content_link && (
                            <a
                              href={activity.content_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="w-3 h-3" />
                              <span>Ver contenido</span>
                            </a>
                          )}
                        </div>
                        
                        {/* Summary preview */}
                        {activity.summary && (
                          <p className="text-sm text-muted-foreground mt-2 line-clamp-2 border-l-2 border-muted pl-2">
                            {activity.summary}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - Historial de Estados */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <History className="w-5 h-5" />
                Historial de Estados
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(!opportunity.state_history || opportunity.state_history.length === 0) ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Sin historial de cambios
                </p>
              ) : (
                <ScrollArea className="h-[400px] pr-4">
                  <div className="space-y-4">
                    {[...opportunity.state_history].reverse().map((change, index) => (
                      <div 
                        key={index}
                        className="relative pl-4 border-l-2 border-muted pb-4 last:pb-0"
                      >
                        <div className="absolute -left-[5px] top-0 w-2 h-2 rounded-full bg-primary" />
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            {change.previous_state ? (
                              <>
                                <Badge variant="outline" className="text-xs">{change.previous_state}</Badge>
                                <span className="text-xs text-muted-foreground">→</span>
                              </>
                            ) : null}
                            <Badge className={stageColors[change.new_state] || 'bg-gray-100'}>
                              {change.new_state}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {formatDateTime(change.timestamp)}
                          </p>
                          <p className="text-sm">
                            Por: {change.changed_by_name}
                          </p>
                          {change.owner && (
                            <p className="text-xs text-muted-foreground">
                              Responsable: {change.owner}
                            </p>
                          )}
                          {change.reason && (
                            <p className="text-sm mt-1 p-2 bg-muted rounded text-muted-foreground">
                              {change.reason}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>

          {/* Metadata */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm text-muted-foreground">Información del Registro</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div>
                <span className="text-muted-foreground">Creado por:</span>
                <span className="ml-2">{opportunity.created_by_name || '-'}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Fecha creación:</span>
                <span className="ml-2">{formatDateTime(opportunity.created_at)}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Dialogs */}
      <OpportunityDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        opportunity={opportunity}
        onSuccess={fetchData}
      />

      <ActivityDialog
        open={activityDialogOpen}
        onOpenChange={setActivityDialogOpen}
        companyId={opportunity.company_id}
        contactId={opportunity.contact_id}
        opportunityId={opportunity.id}
        typsaOrgUnitId={opportunity.typsa_org_unit_id}
        typsaCategory={opportunity.typsa_category}
        onSuccess={fetchData}
      />

      <DeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDelete}
        title="Eliminar Oportunidad"
        description={`¿Estás seguro de que quieres eliminar la oportunidad "${title}"? Esta acción no se puede deshacer.`}
      />

      {/* Stage Change Dialog - Enhanced with state selector and optional comment */}
      <Dialog open={stageChangeDialogOpen} onOpenChange={(open) => {
        setStageChangeDialogOpen(open);
        if (!open) {
          setTargetStage(null);
          setStageChangeData({});
          setStageChangeComment('');
        }
      }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="stage-change-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRight className="w-5 h-5 text-slate-500" />
              Cambiar estado de la oportunidad
            </DialogTitle>
            <DialogDescription>
              {targetStage 
                ? `De "${stage}" a "${targetStage}"`
                : 'Selecciona el nuevo estado para esta oportunidad'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* State selector - shown when no target stage is pre-selected */}
            {!targetStage && (
              <div className="space-y-3">
                <Label className="text-sm font-medium">Nuevo estado</Label>
                <div className="grid grid-cols-2 gap-2">
                  {STAGES.filter(s => s !== stage).map((s) => {
                    const colors = stepperColors[s];
                    const isResultState = s === 'Ganada' || s === 'Perdida' || s === 'Descartada';
                    return (
                      <button
                        key={s}
                        onClick={() => setTargetStage(s)}
                        className={`
                          p-3 rounded-lg border-2 text-left transition-all
                          ${isResultState 
                            ? s === 'Ganada' 
                              ? 'border-emerald-200 hover:border-emerald-400 hover:bg-emerald-50' 
                              : s === 'Perdida'
                                ? 'border-red-200 hover:border-red-400 hover:bg-red-50'
                                : 'border-slate-200 hover:border-slate-400 hover:bg-slate-50'
                            : 'border-slate-200 hover:border-slate-400 hover:bg-slate-50'
                          }
                        `}
                        data-testid={`select-stage-${s.toLowerCase()}`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${colors.active}`} />
                          <span className="font-medium">{s}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Show current transition */}
            {targetStage && (
              <div className="flex items-center justify-center gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${stepperColors[stage]?.active || 'bg-slate-400'}`} />
                  <span className="font-medium text-slate-600">{stage}</span>
                </div>
                <ArrowRight className="w-5 h-5 text-slate-400" />
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${stepperColors[targetStage]?.active || 'bg-slate-400'}`} />
                  <span className="font-semibold text-slate-800">{targetStage}</span>
                </div>
              </div>
            )}
            
            {/* Required fields for the target stage */}
            {targetStage && getStageRequiredFields(targetStage, opportunityScope)?.filter(field => {
              if (field.hideIfTypsa && isTypsaOpportunity) return false;
              if (field.hideIfSolo && (opportunity.bid_modality === 'solo' || stageChangeData.bid_modality === 'solo')) return false;
              if (field.requiresOther && !(stageChangeData.service_types || []).includes('Otros')) return false;
              if (field.showIfMissing) {
                const v = opportunity[field.key];
                if (Array.isArray(v)) return v.length === 0;
                return v === null || v === undefined || v === '';
              }
              if (field.showIfNoOfferedAmount) return !opportunity.offered_amount;
              return true;
            }).length > 0 && (
              <div className="space-y-4">
                <Label className="text-sm font-medium text-slate-700">Campos requeridos</Label>
                {getStageRequiredFields(targetStage, opportunityScope)?.filter(field => {
                  if (field.hideIfTypsa && isTypsaOpportunity) return false;
                  if (field.hideIfSolo && (opportunity.bid_modality === 'solo' || stageChangeData.bid_modality === 'solo')) return false;
                  if (field.requiresOther && !(stageChangeData.service_types || []).includes('Otros')) return false;
                  if (field.showIfMissing) {
                    const v = opportunity[field.key];
                    if (Array.isArray(v)) return v.length === 0;
                    return v === null || v === undefined || v === '';
                  }
                  if (field.showIfNoOfferedAmount) return !opportunity.offered_amount;
                  return true;
                }).map((field) => (
                  <div key={field.key} className="space-y-2">
                    <Label htmlFor={field.key} className="text-sm">{field.label}</Label>
                    
                    {field.type === 'select' ? (
                      <Select
                        value={stageChangeData[field.key] || ''}
                        onValueChange={(value) => setStageChangeData(prev => ({ ...prev, [field.key]: value }))}
                      >
                        <SelectTrigger data-testid={`stage-field-${field.key}`}>
                          <SelectValue placeholder={`Seleccionar ${field.label.toLowerCase()}`} />
                        </SelectTrigger>
                        <SelectContent>
                          {field.options.map((opt) => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : field.type === 'checkbox-list' ? (
                      <div className="grid grid-cols-2 gap-2 p-2 border rounded-md bg-slate-50/40">
                        {(field.options || []).map((opt) => {
                          const selected = (stageChangeData[field.key] || []).includes(opt);
                          return (
                            <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer">
                              <input
                                type="checkbox"
                                className="w-4 h-4"
                                checked={selected}
                                onChange={(e) => {
                                  const cur = stageChangeData[field.key] || [];
                                  if (e.target.checked) {
                                    setStageChangeData(prev => ({ ...prev, [field.key]: [...cur, opt] }));
                                  } else {
                                    setStageChangeData(prev => ({ ...prev, [field.key]: cur.filter(x => x !== opt) }));
                                  }
                                }}
                                data-testid={`stage-field-${field.key}-${opt}`}
                              />
                              <span>{opt}</span>
                            </label>
                          );
                        })}
                      </div>
                    ) : field.type === 'textarea' ? (
                      <Textarea
                        id={field.key}
                        value={stageChangeData[field.key] || ''}
                        onChange={(e) => setStageChangeData(prev => ({ ...prev, [field.key]: e.target.value }))}
                        placeholder={field.label}
                        className="resize-none"
                        data-testid={`stage-field-${field.key}`}
                      />
                    ) : (
                      <Input
                        id={field.key}
                        type={field.type}
                        value={stageChangeData[field.key] || ''}
                        onChange={(e) => setStageChangeData(prev => ({ ...prev, [field.key]: e.target.value }))}
                        placeholder={field.label}
                        data-testid={`stage-field-${field.key}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
            
            {/* Optional comment */}
            {targetStage && (
              <div className="space-y-2">
                <Label htmlFor="stage-comment" className="text-sm text-slate-600">
                  Comentario (opcional)
                </Label>
                <Textarea
                  id="stage-comment"
                  value={stageChangeComment}
                  onChange={(e) => setStageChangeComment(e.target.value)}
                  placeholder="Añade un comentario sobre este cambio de estado..."
                  className="resize-none h-20"
                  data-testid="stage-change-comment"
                />
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setStageChangeDialogOpen(false);
                setTargetStage(null);
                setStageChangeData({});
                setStageChangeComment('');
              }}
            >
              Cancelar
            </Button>
            {targetStage && (
              <Button 
                onClick={handleStageChangeSubmit}
                disabled={stageChangeLoading}
                className={
                  targetStage === 'Ganada' ? 'bg-emerald-600 hover:bg-emerald-700' :
                  targetStage === 'Perdida' ? 'bg-red-600 hover:bg-red-700' :
                  ''
                }
                data-testid="confirm-stage-change-btn"
              >
                {stageChangeLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Confirmar Cambio
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activity Detail Sheet */}
      <Sheet open={activityDetailOpen} onOpenChange={setActivityDetailOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              {selectedActivity?.title || getActivityTypeLabel(selectedActivity?.type)}
            </SheetTitle>
            <SheetDescription>
              Detalle completo de la actividad
            </SheetDescription>
          </SheetHeader>
          
          {selectedActivity && (
            <div className="mt-6 space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="text-sm">
                    {getActivityTypeLabel(selectedActivity.type)}
                  </Badge>
                  {(() => {
                    const isDocumented = isActivityDocumented(selectedActivity);
                    const isPast = new Date(selectedActivity.date) < new Date();
                    if (isPast && !isDocumented) {
                      return (
                        <Badge variant="destructive">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          Sin documentar
                        </Badge>
                      );
                    } else if (isDocumented) {
                      return (
                        <Badge className="bg-green-100 text-green-700">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Documentada
                        </Badge>
                      );
                    }
                    return <Badge variant="secondary">Próxima</Badge>;
                  })()}
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Fecha</label>
                    <p className="text-sm font-medium flex items-center gap-1 mt-1">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      {formatDate(selectedActivity.date)}
                    </p>
                  </div>
                  {selectedActivity.time && (
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Hora</label>
                      <p className="text-sm font-medium flex items-center gap-1 mt-1">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        {selectedActivity.time}
                      </p>
                    </div>
                  )}
                </div>
                
                {selectedActivity.owner && (
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Responsable</label>
                    <p className="text-sm font-medium flex items-center gap-1 mt-1">
                      <User className="w-4 h-4 text-muted-foreground" />
                      {selectedActivity.owner}
                    </p>
                  </div>
                )}
                
                {selectedActivity.location && (
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Ubicación</label>
                    <p className="text-sm font-medium flex items-center gap-1 mt-1">
                      <Building2 className="w-4 h-4 text-muted-foreground" />
                      {selectedActivity.location}
                    </p>
                  </div>
                )}
              </div>
              
              <Separator />
              
              {/* Contacts */}
              {selectedActivity.contact_ids && selectedActivity.contact_ids.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    Contactos ({selectedActivity.contact_ids.length})
                  </label>
                  <div className="mt-2 space-y-2">
                    {selectedActivity.contact_ids.map(contactId => {
                      const contactName = getContactName(contactId);
                      return contactName ? (
                        <div key={contactId} className="flex items-center gap-2 text-sm p-2 bg-muted/50 rounded">
                          <User className="w-4 h-4 text-muted-foreground" />
                          {contactName}
                        </div>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
              
              {/* Description */}
              {selectedActivity.description && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Descripción</label>
                  <p className="text-sm mt-1 p-3 bg-muted/50 rounded whitespace-pre-wrap">
                    {selectedActivity.description}
                  </p>
                </div>
              )}
              
              <Separator />
              
              {/* Documentation Section */}
              <div>
                <label className="text-sm font-semibold flex items-center gap-2 mb-3">
                  <FileText className="w-4 h-4" />
                  Documentación
                </label>
                
                {selectedActivity.summary && (
                  <div className="mb-4">
                    <label className="text-xs font-medium text-muted-foreground">Resumen</label>
                    <p className="text-sm mt-1 p-3 bg-blue-50 border-l-4 border-blue-400 rounded whitespace-pre-wrap">
                      {selectedActivity.summary}
                    </p>
                  </div>
                )}
                
                {selectedActivity.notes && (
                  <div className="mb-4">
                    <label className="text-xs font-medium text-muted-foreground">Notas / Transcripción</label>
                    <p className="text-sm mt-1 p-3 bg-muted/50 rounded whitespace-pre-wrap">
                      {selectedActivity.notes}
                    </p>
                  </div>
                )}
                
                {selectedActivity.key_points && selectedActivity.key_points.length > 0 && (
                  <div className="mb-4">
                    <label className="text-xs font-medium text-muted-foreground">
                      Puntos Clave ({selectedActivity.key_points.length})
                    </label>
                    <ul className="mt-2 space-y-1">
                      {selectedActivity.key_points.map((point, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm p-2 bg-amber-50 border-l-2 border-amber-400 rounded">
                          <span className="text-amber-600 font-medium">{idx + 1}.</span>
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {selectedActivity.decisions && selectedActivity.decisions.length > 0 && (
                  <div className="mb-4">
                    <label className="text-xs font-medium text-muted-foreground">
                      Decisiones ({selectedActivity.decisions.length})
                    </label>
                    <ul className="mt-2 space-y-1">
                      {selectedActivity.decisions.map((decision, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm p-2 bg-green-50 border-l-2 border-green-400 rounded">
                          <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span>{decision}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {selectedActivity.next_steps && selectedActivity.next_steps.length > 0 && (
                  <div className="mb-4">
                    <label className="text-xs font-medium text-muted-foreground">
                      Próximos Pasos ({selectedActivity.next_steps.length})
                    </label>
                    <ul className="mt-2 space-y-1">
                      {selectedActivity.next_steps.map((step, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm p-2 bg-purple-50 border-l-2 border-purple-400 rounded">
                          <ArrowRight className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                          <span>{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {selectedActivity.risks_concerns && selectedActivity.risks_concerns.length > 0 && (
                  <div className="mb-4">
                    <label className="text-xs font-medium text-muted-foreground">
                      Riesgos / Preocupaciones ({selectedActivity.risks_concerns.length})
                    </label>
                    <ul className="mt-2 space-y-1">
                      {selectedActivity.risks_concerns.map((risk, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm p-2 bg-red-50 border-l-2 border-red-400 rounded">
                          <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                          <span>{risk}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {selectedActivity.opportunity_signals && selectedActivity.opportunity_signals.length > 0 && (
                  <div className="mb-4">
                    <label className="text-xs font-medium text-muted-foreground">
                      Señales de Oportunidad ({selectedActivity.opportunity_signals.length})
                    </label>
                    <ul className="mt-2 space-y-1">
                      {selectedActivity.opportunity_signals.map((signal, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm p-2 bg-blue-50 border-l-2 border-blue-400 rounded">
                          <Lightbulb className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                          <span>{signal}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {!isActivityDocumented(selectedActivity) && (
                  <div className="text-center py-4 text-muted-foreground">
                    <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Esta actividad aún no está documentada</p>
                  </div>
                )}
              </div>
              
              {/* Footer Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => setActivityDetailOpen(false)}
                >
                  Cerrar
                </Button>
                <Button 
                  onClick={() => {
                    setActivityDetailOpen(false);
                    navigate(`/activities?highlight=${selectedActivity.id}`);
                  }}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Ver en Actividades
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default OpportunityDetail;
